import { useRef, useEffect, useCallback, useState } from 'react';
import * as THREE from 'three';
import type { GraphNode, GraphEdge } from '../../types';

// ── Props ──

interface Graph3DProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  onNodeClick?: (node: GraphNode) => void;
  onNodeHover?: (node: GraphNode | null) => void;
  selectedNodeId?: string;
}

// ── Entity Colors ──

const ENTITY_COLORS: Record<string, string> = {
  person: '#3b82f6',
  organization: '#a855f7',
  location: '#22c55e',
  ip_address: '#ef4444',
  domain: '#f97316',
  email: '#14b8a6',
  phone_number: '#06b6d4',
  financial_account: '#eab308',
  vehicle: '#64748b',
};

const DEFAULT_COLOR = '#64748b';
const HIGHLIGHT_COLOR = '#00f0ff';
const EDGE_COLOR = 0x2d4a6f;
const EDGE_HIGHLIGHT_COLOR = 0x00f0ff;
const BG_COLOR = 0x0b1120;

// ── Force Layout Parameters ──

const REPULSION_STRENGTH = 200;
const ATTRACTION_STRENGTH = 0.005;
const IDEAL_EDGE_LENGTH = 15;
const CENTRAL_GRAVITY = 0.002;
const DAMPING = 0.92;
const SIM_ITERATIONS_PER_FRAME = 3;
const SIMULATION_FRAMES = 300;

// ── Label texture helper ──

function createLabelTexture(text: string, color: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const fontSize = 48;
  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;
  const metrics = ctx.measureText(text);
  const width = Math.ceil(metrics.width) + 24;
  const height = fontSize + 16;
  canvas.width = width;
  canvas.height = height;

  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(text, width / 2, height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

// ── Simulation Node ──

interface SimNode {
  node: GraphNode;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  mesh: THREE.Mesh;
  label: THREE.Sprite;
  radius: number;
}

export default function Graph3D({ nodes, edges, onNodeClick, onNodeHover }: Graph3DProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const simNodesRef = useRef<SimNode[]>([]);
  const edgeLinesRef = useRef<THREE.LineSegments | null>(null);
  const frameRef = useRef(0);
  const simFrameRef = useRef(0);
  const hoveredRef = useRef<SimNode | null>(null);
  const animIdRef = useRef(0);
  const mouseRef = useRef(new THREE.Vector2(9999, 9999));
  const raycasterRef = useRef(new THREE.Raycaster());

  // Orbit control state
  const isDraggingRef = useRef(false);
  const prevMouseRef = useRef({ x: 0, y: 0 });
  const sphericalRef = useRef(new THREE.Spherical(60, Math.PI / 2, 0));
  const targetRef = useRef(new THREE.Vector3(0, 0, 0));
  const cameraTargetRef = useRef<THREE.Vector3 | null>(null);
  const cameraTargetSphere = useRef<THREE.Spherical | null>(null);

  // Edge adjacency for highlighting
  const adjacencyRef = useRef<Map<string, Set<string>>>(new Map());

  const [, setForceRender] = useState(0);

  // ── Build adjacency map ──
  const buildAdjacency = useCallback(() => {
    const map = new Map<string, Set<string>>();
    for (const edge of edges) {
      if (!map.has(edge.source)) map.set(edge.source, new Set());
      if (!map.has(edge.target)) map.set(edge.target, new Set());
      map.get(edge.source)!.add(edge.target);
      map.get(edge.target)!.add(edge.source);
    }
    adjacencyRef.current = map;
  }, [edges]);

  // ── Initialize scene ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(BG_COLOR);
    scene.fog = new THREE.Fog(BG_COLOR, 80, 250);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      60,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    camera.position.setFromSpherical(sphericalRef.current);
    camera.lookAt(targetRef.current);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);
    const point1 = new THREE.PointLight(0xffffff, 0.8);
    point1.position.set(50, 50, 50);
    scene.add(point1);
    const point2 = new THREE.PointLight(0x00f0ff, 0.3);
    point2.position.set(-50, -50, -50);
    scene.add(point2);

    // Resize
    const onResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(animIdRef.current);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  // ── Build graph objects when nodes/edges change ──
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    buildAdjacency();

    // Clear previous objects
    for (const sn of simNodesRef.current) {
      scene.remove(sn.mesh);
      scene.remove(sn.label);
      sn.mesh.geometry.dispose();
      (sn.mesh.material as THREE.Material).dispose();
      sn.label.material.map?.dispose();
      (sn.label.material as THREE.Material).dispose();
    }
    if (edgeLinesRef.current) {
      scene.remove(edgeLinesRef.current);
      edgeLinesRef.current.geometry.dispose();
      (edgeLinesRef.current.material as THREE.Material).dispose();
      edgeLinesRef.current = null;
    }

    // Create sim nodes
    const spread = Math.max(20, Math.sqrt(nodes.length) * 6);
    const simNodes: SimNode[] = nodes.map((node, i) => {
      const phi = Math.acos(-1 + (2 * i + 1) / (nodes.length + 1));
      const theta = Math.sqrt(nodes.length * Math.PI) * phi;
      const position = new THREE.Vector3(
        spread * Math.cos(theta) * Math.sin(phi) * (0.8 + Math.random() * 0.4),
        spread * Math.sin(theta) * Math.sin(phi) * (0.8 + Math.random() * 0.4),
        spread * Math.cos(phi) * (0.8 + Math.random() * 0.4)
      );

      const radius = 0.4 + (node.risk_score || 0) * 0.6;
      const color = ENTITY_COLORS[node.type] || DEFAULT_COLOR;

      // Sphere mesh
      const geo = new THREE.SphereGeometry(radius, 24, 24);
      const mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(color),
        emissive: new THREE.Color(color),
        emissiveIntensity: 0.15,
        transparent: true,
        opacity: 0.9,
        roughness: 0.4,
        metalness: 0.3,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(position);
      mesh.userData = { nodeId: node.id };
      scene.add(mesh);

      // Billboard label
      const labelText =
        node.label.length > 18 ? node.label.substring(0, 18) + '...' : node.label;
      const texture = createLabelTexture(labelText, '#94a3b8');
      const spriteMat = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(spriteMat);
      const aspect = texture.image.width / texture.image.height;
      sprite.scale.set(aspect * 1.2, 1.2, 1);
      sprite.position.copy(position);
      sprite.position.y += radius + 1.0;
      scene.add(sprite);

      return {
        node,
        position,
        velocity: new THREE.Vector3(0, 0, 0),
        mesh,
        label: sprite,
        radius,
      };
    });
    simNodesRef.current = simNodes;

    // Node ID -> sim index
    const nodeIndexMap = new Map(simNodes.map((sn, i) => [sn.node.id, i]));

    // Create edge geometry
    const edgePositions = new Float32Array(edges.length * 6);
    const edgeColors = new Float32Array(edges.length * 6);
    const baseColor = new THREE.Color(EDGE_COLOR);
    for (let i = 0; i < edges.length; i++) {
      const srcIdx = nodeIndexMap.get(edges[i].source);
      const tgtIdx = nodeIndexMap.get(edges[i].target);
      if (srcIdx === undefined || tgtIdx === undefined) continue;
      const src = simNodes[srcIdx].position;
      const tgt = simNodes[tgtIdx].position;
      const offset = i * 6;
      edgePositions[offset] = src.x;
      edgePositions[offset + 1] = src.y;
      edgePositions[offset + 2] = src.z;
      edgePositions[offset + 3] = tgt.x;
      edgePositions[offset + 4] = tgt.y;
      edgePositions[offset + 5] = tgt.z;
      for (let c = 0; c < 6; c += 3) {
        edgeColors[offset + c] = baseColor.r;
        edgeColors[offset + c + 1] = baseColor.g;
        edgeColors[offset + c + 2] = baseColor.b;
      }
    }
    const edgeGeo = new THREE.BufferGeometry();
    edgeGeo.setAttribute('position', new THREE.BufferAttribute(edgePositions, 3));
    edgeGeo.setAttribute('color', new THREE.BufferAttribute(edgeColors, 3));
    const edgeMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.5,
    });
    const lineSegments = new THREE.LineSegments(edgeGeo, edgeMat);
    scene.add(lineSegments);
    edgeLinesRef.current = lineSegments;

    // Reset simulation
    simFrameRef.current = 0;

    setForceRender((v) => v + 1);
  }, [nodes, edges, buildAdjacency]);

  // ── Animation loop ──
  useEffect(() => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    if (!scene || !camera || !renderer) return;

    const nodeIndexMap = new Map(
      simNodesRef.current.map((sn, i) => [sn.node.id, i])
    );
    const tempForce = new THREE.Vector3();
    const diff = new THREE.Vector3();

    const animate = () => {
      animIdRef.current = requestAnimationFrame(animate);
      frameRef.current++;

      const simNodes = simNodesRef.current;
      const lineSegs = edgeLinesRef.current;

      // ── Force simulation ──
      if (simFrameRef.current < SIMULATION_FRAMES && simNodes.length > 0) {
        for (let iter = 0; iter < SIM_ITERATIONS_PER_FRAME; iter++) {
          // Repulsion (Barnes-Hut approximation: skip distant pairs for perf)
          for (let i = 0; i < simNodes.length; i++) {
            tempForce.set(0, 0, 0);
            const pi = simNodes[i].position;

            for (let j = 0; j < simNodes.length; j++) {
              if (i === j) continue;
              diff.subVectors(pi, simNodes[j].position);
              const distSq = diff.lengthSq();
              const dist = Math.max(Math.sqrt(distSq), 0.5);
              const force = REPULSION_STRENGTH / distSq;
              tempForce.addScaledVector(diff, force / dist);
            }

            // Central gravity
            tempForce.addScaledVector(pi, -CENTRAL_GRAVITY);

            simNodes[i].velocity.add(tempForce);
          }

          // Attraction along edges
          for (const edge of edges) {
            const srcIdx = nodeIndexMap.get(edge.source);
            const tgtIdx = nodeIndexMap.get(edge.target);
            if (srcIdx === undefined || tgtIdx === undefined) continue;
            diff.subVectors(
              simNodes[tgtIdx].position,
              simNodes[srcIdx].position
            );
            const dist = diff.length();
            const force = (dist - IDEAL_EDGE_LENGTH) * ATTRACTION_STRENGTH;
            diff.normalize().multiplyScalar(force);
            simNodes[srcIdx].velocity.add(diff);
            simNodes[tgtIdx].velocity.addScaledVector(diff, -1);
          }

          // Apply velocity with damping
          for (const sn of simNodes) {
            sn.velocity.multiplyScalar(DAMPING);
            sn.position.add(sn.velocity);
          }
        }
        simFrameRef.current += SIM_ITERATIONS_PER_FRAME;
      }

      // ── Update visual positions ──
      for (const sn of simNodes) {
        sn.mesh.position.copy(sn.position);
        sn.label.position.copy(sn.position);
        sn.label.position.y += sn.radius + 1.0;
      }

      // Update edge positions
      if (lineSegs) {
        const posAttr = lineSegs.geometry.getAttribute(
          'position'
        ) as THREE.BufferAttribute;
        for (let i = 0; i < edges.length; i++) {
          const srcIdx = nodeIndexMap.get(edges[i].source);
          const tgtIdx = nodeIndexMap.get(edges[i].target);
          if (srcIdx === undefined || tgtIdx === undefined) continue;
          const src = simNodes[srcIdx].position;
          const tgt = simNodes[tgtIdx].position;
          const offset = i * 6;
          posAttr.array[offset] = src.x;
          posAttr.array[offset + 1] = src.y;
          posAttr.array[offset + 2] = src.z;
          posAttr.array[offset + 3] = tgt.x;
          posAttr.array[offset + 4] = tgt.y;
          posAttr.array[offset + 5] = tgt.z;
        }
        posAttr.needsUpdate = true;
      }

      // ── Raycasting for hover ──
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const meshes = simNodes.map((sn) => sn.mesh);
      const intersects = raycasterRef.current.intersectObjects(meshes, false);

      const prevHovered = hoveredRef.current;
      let newHovered: SimNode | null = null;

      if (intersects.length > 0) {
        const hitId = intersects[0].object.userData.nodeId;
        newHovered = simNodes.find((sn) => sn.node.id === hitId) || null;
      }

      if (newHovered !== prevHovered) {
        // Reset previous highlight
        if (prevHovered) {
          const prevColor = ENTITY_COLORS[prevHovered.node.type] || DEFAULT_COLOR;
          (prevHovered.mesh.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.15;
          (prevHovered.mesh.material as THREE.MeshStandardMaterial).emissive.set(prevColor);

          // Reset connected nodes
          const connected = adjacencyRef.current.get(prevHovered.node.id);
          if (connected) {
            for (const connId of connected) {
              const connSn = simNodes.find((sn) => sn.node.id === connId);
              if (connSn) {
                const c = ENTITY_COLORS[connSn.node.type] || DEFAULT_COLOR;
                (connSn.mesh.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.15;
                (connSn.mesh.material as THREE.MeshStandardMaterial).emissive.set(c);
              }
            }
          }

          // Reset edge colors
          if (lineSegs) {
            const colorAttr = lineSegs.geometry.getAttribute('color') as THREE.BufferAttribute;
            const base = new THREE.Color(EDGE_COLOR);
            for (let i = 0; i < edges.length; i++) {
              const offset = i * 6;
              for (let c = 0; c < 6; c += 3) {
                colorAttr.array[offset + c] = base.r;
                colorAttr.array[offset + c + 1] = base.g;
                colorAttr.array[offset + c + 2] = base.b;
              }
            }
            colorAttr.needsUpdate = true;
          }
        }

        // Apply new highlight
        if (newHovered) {
          (newHovered.mesh.material as THREE.MeshStandardMaterial).emissive.set(HIGHLIGHT_COLOR);
          (newHovered.mesh.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.7;

          const connected = adjacencyRef.current.get(newHovered.node.id);
          if (connected) {
            for (const connId of connected) {
              const connSn = simNodes.find((sn) => sn.node.id === connId);
              if (connSn) {
                (connSn.mesh.material as THREE.MeshStandardMaterial).emissive.set(HIGHLIGHT_COLOR);
                (connSn.mesh.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5;
              }
            }

            // Highlight connected edges
            if (lineSegs) {
              const colorAttr = lineSegs.geometry.getAttribute('color') as THREE.BufferAttribute;
              const hlColor = new THREE.Color(EDGE_HIGHLIGHT_COLOR);
              for (let i = 0; i < edges.length; i++) {
                const e = edges[i];
                if (
                  e.source === newHovered.node.id ||
                  e.target === newHovered.node.id
                ) {
                  const offset = i * 6;
                  for (let c = 0; c < 6; c += 3) {
                    colorAttr.array[offset + c] = hlColor.r;
                    colorAttr.array[offset + c + 1] = hlColor.g;
                    colorAttr.array[offset + c + 2] = hlColor.b;
                  }
                }
              }
              colorAttr.needsUpdate = true;
            }
          }

          onNodeHover?.(newHovered.node);
        } else {
          onNodeHover?.(null);
        }

        hoveredRef.current = newHovered;
      }

      // Set cursor
      if (containerRef.current) {
        containerRef.current.style.cursor = newHovered ? 'pointer' : 'default';
      }

      // ── Smooth camera transition ──
      if (cameraTargetRef.current) {
        targetRef.current.lerp(cameraTargetRef.current, 0.05);
        if (targetRef.current.distanceTo(cameraTargetRef.current) < 0.1) {
          targetRef.current.copy(cameraTargetRef.current);
          cameraTargetRef.current = null;
        }
      }
      if (cameraTargetSphere.current) {
        const cur = sphericalRef.current;
        cur.radius += (cameraTargetSphere.current.radius - cur.radius) * 0.05;
        cur.phi += (cameraTargetSphere.current.phi - cur.phi) * 0.05;
        cur.theta += (cameraTargetSphere.current.theta - cur.theta) * 0.05;
        if (
          Math.abs(cur.radius - cameraTargetSphere.current.radius) < 0.1 &&
          Math.abs(cur.phi - cameraTargetSphere.current.phi) < 0.001 &&
          Math.abs(cur.theta - cameraTargetSphere.current.theta) < 0.001
        ) {
          cameraTargetSphere.current = null;
        }
      }

      // Update camera from spherical
      const offset = new THREE.Vector3().setFromSpherical(sphericalRef.current);
      camera.position.copy(targetRef.current).add(offset);
      camera.lookAt(targetRef.current);

      renderer.render(scene, camera);
    };

    animIdRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animIdRef.current);
  }, [nodes, edges, onNodeHover, onNodeClick]);

  // ── Mouse / touch handlers ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getCanvasCoords = (clientX: number, clientY: number) => {
      const rect = container.getBoundingClientRect();
      return new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      );
    };

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current = getCanvasCoords(e.clientX, e.clientY);

      if (isDraggingRef.current) {
        const dx = e.clientX - prevMouseRef.current.x;
        const dy = e.clientY - prevMouseRef.current.y;
        sphericalRef.current.theta -= dx * 0.005;
        sphericalRef.current.phi -= dy * 0.005;
        sphericalRef.current.phi = Math.max(
          0.1,
          Math.min(Math.PI - 0.1, sphericalRef.current.phi)
        );
        prevMouseRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 0) {
        isDraggingRef.current = true;
        prevMouseRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      if (e.button === 0) {
        // Only trigger click if we didn't drag significantly
        const dx = Math.abs(e.clientX - prevMouseRef.current.x);
        const dy = Math.abs(e.clientY - prevMouseRef.current.y);
        if (dx < 5 && dy < 5 && hoveredRef.current && onNodeClick) {
          const clicked = hoveredRef.current;
          onNodeClick(clicked.node);

          // Smooth camera transition to clicked node
          cameraTargetRef.current = clicked.position.clone();
          const dist = Math.max(clicked.radius * 8, 20);
          cameraTargetSphere.current = new THREE.Spherical(
            dist,
            sphericalRef.current.phi,
            sphericalRef.current.theta
          );
        }
        isDraggingRef.current = false;
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoom = e.deltaY * 0.05;
      sphericalRef.current.radius = Math.max(
        5,
        Math.min(250, sphericalRef.current.radius + zoom)
      );
    };

    const onMouseLeave = () => {
      isDraggingRef.current = false;
      mouseRef.current.set(9999, 9999);
    };

    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mouseup', onMouseUp);
    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('mouseleave', onMouseLeave);

    return () => {
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [onNodeClick]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full min-h-[400px]"
      style={{ background: '#0b1120' }}
    />
  );
}
